import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface MenuState {
  open: boolean;
  docked: boolean;
  openMenu: () => void;
  closeMenu: () => void;
}

const MenuContext = createContext<MenuState | null>(null);

/**
 * The menu (drawer) is open for the location it was opened on: any navigation
 * closes it, as a choice in the native drawer does, without an effect.
 */
export function MenuProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  // The existing md breakpoint: phones reveal a drawer; larger windows dock it.
  const docked = useMediaQuery("(min-width: 768px)");
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const open = !docked && openedAt === location.key;

  // Resizing back to a phone must not revive a previously open drawer.
  if (docked && openedAt !== null) setOpenedAt(null);

  const openMenu = useCallback(() => setOpenedAt(location.key), [location.key]);
  const closeMenu = useCallback(() => setOpenedAt(null), []);

  const value = useMemo(() => ({ open, docked, openMenu, closeMenu }), [open, docked, openMenu, closeMenu]);
  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
}

/** Null outside the signed-in shell. */
export const useMenu = () => useContext(MenuContext);
